import { guideFor, pitchGauge, PITCH_BAR, type ScenarioKey } from '../capture/guide';
import type { Orientation, OrientationLimits } from '../capture/orientation';

// Gli elementi grafici della guida (capture/guide.ts), condivisi da recorder e schermata operatore. I due <g> vanno DENTRO un <svg>
// con viewBox 300×400 e `preserveAspectRatio="xMidYMid meet"` posato sul video 3:4, così restano allineati all'immagine.

/** Il disegno di dove deve stare il pallet nell'inquadratura, per lo scenario. */
export function ScenarioOutline({ scenario }: { scenario: ScenarioKey }) {
  const guide = guideFor(scenario);
  return (
    <g fill="none" stroke="#FFFFFF" strokeOpacity={0.8} strokeLinejoin="round" strokeLinecap="round" data-guide={scenario}>
      {guide.shapes.map((shape, i) => {
        const points = shape.points.map(([x, y]) => `${x},${y}`).join(' ');
        const style = { strokeWidth: shape.dashed ? 2 : 4, strokeDasharray: shape.dashed ? '8 6' : undefined };
        return shape.closed ? <polygon key={i} points={points} {...style} /> : <polyline key={i} points={points} {...style} />;
      })}
    </g>
  );
}

/** La livella: una barra a sinistra con la zona buona in verde e un segno che sale e scende con l'inclinazione. */
export function PitchGauge({ orientation, limits }: { orientation: Orientation | null; limits: OrientationLimits }) {
  const g = pitchGauge(orientation, limits);
  const x = PITCH_BAR.x;
  return (
    <g data-gauge>
      <line x1={x} y1={g.barTopPx} x2={x} y2={g.barBottomPx} stroke="#FFFFFF" strokeOpacity={0.3} strokeWidth={6} strokeLinecap="round" />
      <line x1={x} y1={g.zoneTopPx} x2={x} y2={g.zoneBottomPx} stroke="#34C759" strokeOpacity={0.85} strokeWidth={6} strokeLinecap="round" />
      {g.markerPx !== null && (
        <>
          <circle cx={x} cy={g.markerPx} r={7} fill={g.inZone ? '#34C759' : '#FF9500'} stroke="#FFFFFF" strokeWidth={2} />
          <text x={x + 12} y={g.markerPx + 4} fontSize={12} fill="#FFFFFF" fillOpacity={0.9}>
            {Math.round(orientation!.pitchDownDeg)}°
          </text>
        </>
      )}
    </g>
  );
}

/** Due pulsanti per scegliere lo scenario della guida. */
export function ScenarioToggle({ value, onChange }: { value: ScenarioKey; onChange: (s: ScenarioKey) => void }) {
  return (
    <span className="flex gap-1.5" role="group" aria-label="Scenario">
      {(['Postazione', 'Scaffale'] as const).map((s) => (
        <button
          key={s}
          type="button"
          aria-pressed={s === value}
          onClick={() => onChange(s)}
          className={`min-h-[32px] rounded-lg px-2.5 text-[13px] font-semibold active:opacity-70 ${s === value ? 'bg-white text-black' : 'bg-white/25 text-white'}`}
        >
          {s}
        </button>
      ))}
    </span>
  );
}
